import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

function renderWelcome(text) {
  return text.split("\n").map((line, i) => (
    <p key={`${i}-${line}`}>
      {line.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
        part.startsWith("**") && part.endsWith("**") ? (
          <strong key={`${j}-${part}`} className="text-white">{part.slice(2, -2)}</strong>
        ) : (
          part
        )
      )}
    </p>
  ));
}

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ username: "", email: "", password: "", team_name: "" });

  const { data: settings } = useQuery({
    queryKey: ["settings-public"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const b = settings?.branding || {};

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Bentornato in pista!");
      } else {
        await register(form);
        toast.success("Registrazione completata. Benvenuto al FantasyRace!");
      }
      navigate(next || "/");
    } catch (err) {
      toast.error(errMsg(err, "Operazione fallita"));
    } finally {
      setLoading(false);
    }
  };

  const bgStyle = {};
  if (b.login_bg_type === "image" && b.login_bg_image) {
    bgStyle.backgroundImage = `url(${b.login_bg_image})`;
    bgStyle.backgroundSize = "cover";
    bgStyle.backgroundPosition = "center";
  } else if (b.login_bg_type === "gradient") {
    bgStyle.background = `linear-gradient(${b.login_gradient_angle ?? 135}deg, ${b.login_gradient_from || "#121214"}, ${b.login_gradient_to || "#3F0713"})`;
  } else if (b.login_bg_color) {
    bgStyle.background = b.login_bg_color;
  }

  const cardStyle = {
    opacity: (b.login_card_opacity ?? 100) / 100,
    borderRadius: `${b.login_card_radius ?? 12}px`,
  };

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-4 py-2.5 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport login-input";

  return (
    <div className="min-h-screen bg-[#121214] flex flex-col relative" style={bgStyle} data-testid="login-page">
      {b.login_bg_type === "image" && b.login_bg_image && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `rgba(0,0,0,${(b.login_overlay_opacity ?? 55) / 100})` }}
          data-testid="login-overlay"
        />
      )}
      {b.login_input_color && (
        <style>{`.login-input { background-color: ${b.login_input_color} !important; }`}</style>
      )}
      <div className="checkered-strip relative z-10" />
      <div className="flex-1 flex relative z-10">
        <div className="hidden lg:flex flex-1 checkered-fade flex-col justify-center px-16 border-r border-[#3F3F4C]">
          <Flag className="w-10 h-10 text-[#FF1E27] mb-6" />
          <h1 className="font-racing font-black text-4xl xl:text-5xl uppercase text-white leading-tight">
            Fantasy<span className="text-[#FF1E27]">Race</span>
          </h1>
          <p className="font-body-sport text-[#FFB703] tracking-[0.3em] text-sm mt-3 uppercase">
            69° Salita dei Monti Iblei
          </p>
          <p className="text-gray-400 mt-6 max-w-md text-sm leading-relaxed">
            Schiera 18 piloti della cronoscalata, scegli il tuo Capitano e scala la classifica.
            120 crediti. Una sola vetta: Chiaramonte Gulfi.
          </p>
        </div>

        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div
            className="w-full max-w-md rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] p-8"
            style={cardStyle}
            data-testid="login-card"
          >
            {b.login_welcome_message && (
              <div
                data-testid="login-welcome"
                className="mb-6 text-sm text-gray-300 font-body-sport leading-relaxed space-y-1"
              >
                {renderWelcome(b.login_welcome_message)}
              </div>
            )}
            <div className="flex rounded-lg overflow-hidden border border-[#3F3F4C] mb-8">
              <button
                data-testid="login-tab-button"
                onClick={() => setMode("login")}
                className={`flex-1 py-2 font-body-sport text-xs font-bold tracking-widest uppercase transition-colors ${
                  mode === "login" ? "bg-[#FF1E27] text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                Accedi
              </button>
              <button
                data-testid="register-tab-button"
                onClick={() => setMode("register")}
                className={`flex-1 py-2 font-body-sport text-xs font-bold tracking-widest uppercase transition-colors ${
                  mode === "register" ? "bg-[#FF1E27] text-white" : "text-gray-400 hover:text-white"
                }`}
              >
                Registrati
              </button>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <>
                  <input
                    data-testid="register-username-input"
                    className={inputCls}
                    placeholder="Username"
                    value={form.username}
                    onChange={set("username")}
                    required
                  />
                  <input
                    data-testid="register-team-input"
                    className={inputCls}
                    placeholder="Nome della tua scuderia"
                    value={form.team_name}
                    onChange={set("team_name")}
                  />
                </>
              )}
              <input
                data-testid="email-input"
                type="email"
                className={inputCls}
                placeholder="Email"
                value={form.email}
                onChange={set("email")}
                required
              />
              <input
                data-testid="password-input"
                type="password"
                className={inputCls}
                placeholder="Password"
                value={form.password}
                onChange={set("password")}
                required
              />
              <button
                data-testid="auth-submit-button"
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] disabled:opacity-50 transition-colors py-3 font-racing font-bold text-sm tracking-widest text-white uppercase"
              >
                {loading ? "..." : { login: "Entra in Pista", register: "Crea la tua Scuderia" }[mode]}
              </button>
            </form>
            <p className="text-center text-[10px] text-gray-600 mt-6 font-body-sport tracking-widest uppercase">
              <Link to="/reset-password" data-testid="forgot-password-link" className="hover:text-[#FFB703] transition-colors">
                Password dimenticata? Reimpostala via email
              </Link>
            </p>
          </div>
        </div>
      </div>
      <div className="checkered-strip relative z-10" />
    </div>
  );
}
